import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './hooks/useAuth';
// TS Cache Buster v2.4.2 (Force Hub Refresh)
import { HubApp } from './apps/hub/HubApp';
import { LoginPortal } from './apps/hub/LoginPortal';
import { RegisterPortal } from './apps/hub/RegisterPortal';
import MasterPlanApp from './apps/master-plan/MasterPlanApp';
import HealthTrackerApp from './apps/blood-pressure/HealthTrackerApp';
import { useLocalStorage } from './hooks/useLocalStorage';

/**
 * App: The Root Multi-App Orchestrator.
 * Handles top-level routing, authentication states, and app-switching.
 * SHADOW VERSION (.tsx)
 */
const App: React.FC = () => {
    const { user, loading } = useAuth();
    
    // PERSISTENCE: Remember where the user left off
    const [activeApp, setActiveApp] = useLocalStorage('mp-active-app', 'hub');
    const [navState, setNavState] = useLocalStorage<any>('mp-nav-state', { view: null, state: null });

    const [showRegister, setShowRegister] = useState<boolean>(false);

    // GENESIS PORTAL: Detect secret registration path
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('portal') === 'genesis') {
            setShowRegister(true);
        }
    }, []);

    const navigate = useCallback((appId: string, view: string | null = null, state: any = null) => {
        setActiveApp(appId);
        setNavState({ view, state });
    }, [setActiveApp, setNavState]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-black">
                <div className="animate-pulse text-orange-500 font-black tracking-tighter text-2xl">
                    SYSTEM INITIALIZING...
                </div>
            </div>
        );
    }

    if (!user) {
        return showRegister ? (
            <RegisterPortal onBack={() => setShowRegister(false)} />
        ) : (
            <LoginPortal onRegister={() => setShowRegister(true)} />
        );
    }

    // --- MAIN ROUTER ---
    return (
        <>
            {activeApp === 'regimen' && (
                <MasterPlanApp 
                    onExit={() => navigate('hub')} 
                    currentView={navState.view}
                    navState={navState.state}
                    onNavigate={(view, state) => navigate('regimen', view, state)} 
                />
            )}
            {activeApp === 'bp' && (
                <HealthTrackerApp onExit={() => navigate('hub')} />
            )}
            {activeApp === 'hub' && <HubApp setActiveApp={(id: string) => navigate(id)} />}
        </>
    );
};

export default App;
